import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, HelpCircle, ArrowRight } from 'lucide-react';

const FAQPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "How does the pricing and billing work?",
      answer: "We offer a Standard plan (from $11/mo) and a Pro plan (from $22/mo). You can add multiple organisations (ABNs) to your account and receive a 20% discount on each add-on. We also offer a secure Account Freeze option for just $4/month if you need to pause your subscription without losing your data."
    },
    {
      question: "Can I manage more than one ABN?",
      answer: "Yes. The Lucent Sky dashboard allows users to switch between organisations quickly. Adding new entities is simple and helps keep distinct operational boundaries for accurate reporting across different branches or ABNs."
    },
    {
      question: "I have businesses in different States. Does the math change?",
      answer: "Absolutely. Each State and Territory has a different \"Grid Factor\" based on their energy mix. Lucent Sky ESG automatically applies the correct National Greenhouse Accounts factor based on the State you select for each organisation."
    },
    {
      question: "Are the emission factors kept up to date?",
      answer: "Yes. Lucent Sky ESG is designed to stay up-to-date with annual changes to the frameworks. The system automatically adds new National Greenhouse Accounts (NGA) emission factors annually, and these factors are precision-applied based on the specific date the emission occurred."
    },
    {
      question: "Can I proportion an emission that spans multiple years?",
      answer: "Yes. If you receive a bill that spans the end of one financial year and the beginning of another, the tool caters for this and allows you to apportion the emissions accurately across both reporting periods."
    },
    {
      question: "Can I quickly view data from different financial years?",
      answer: "Yes! There is a quick toggle on the dashboard that allows you to instantly switch your view to show data from different financial years, making year-on-year comparisons incredibly easy."
    },
    {
      question: "Can I add evidence like bills or receipts?",
      answer: "Yes. Every emission entry allows you to attach digital evidence (such as a PDF bill or a photo of a receipt). This built-in 'Evidence Vault' ensures you always have the required documentation on hand if you are ever audited."
    },
    {
      question: "Can I invite my team to help manage the data?",
      answer: "Yes, you can invite other users to view or maintain your organisation's data. Adding users does not cost anything extra, although standard seat limits apply depending on your plan tier."
    },
    {
      question: "What is the AI Co-pilot included in the Pro plan?",
      answer: "The AI Co-pilot is an advanced assistant built directly into the Pro subscription. It can help you answer any ESG-related questions, guide you through the dashboard features, and even assist with questions relating to your final reports."
    },
    {
      question: "What other support and resources are available?",
      answer: "Beyond our responsive email support, the platform features comprehensive Help Guides, the interactive AI Co-pilot, an Ideas Forum where you can request features, and a secure Messaging Centre to communicate directly with our team."
    },
    {
      question: "A large corporate client is asking for my carbon data. Can Lucent Sky ESG help?",
      answer: "Absolutely. This is exactly why we built the platform. Large corporations and government agencies are increasingly required to report their Scope 3 emissions – which includes the carbon footprint of their suppliers (that's you). Lucent Sky ESG gives you a verified, state-specific emissions report that you can share via a secure Public Verification URL. Think of it as your \"Green Passport\" – a professional, third-party-organised summary that your clients can trust, without you having to manually compile spreadsheets or explain calculation methodologies."
    },
    {
      question: "What emission categories can I track?",
      answer: "Lucent Sky ESG currently supports Scope-1 & Scope-2 emissions across five main categories: Electricity (kWh), Natural Gas (GJ), Petrol (litres), Diesel (litres) & LPG (litres). Each category has specific emission factors from the National Greenhouse Accounts that vary by State/Territory for electricity."
    },
    {
      question: "How do I bulk import data?",
      answer: "Navigate to Dashboard → Import Data. Download our CSV template with columns: Date, Category, Quantity, Unit, Reference. Fill in your data and upload. The system validates units and automatically applies the correct State-specific NGA emission factors."
    },
    {
      question: "What's included in the audit-ready PDF report?",
      answer: "Professional reports include: Executive Summary with total tCO₂-e, visual charts (pie, bar, category breakdown), detailed data table, methodology page citing official NGA factors, evidence index listing all uploaded documents, and a QR code linking to your public verification profile. On our Pro plan, you can also access Custom Report Builders and comprehensive AI narratives."
    },
    {
      question: "How do Custom Reports work on the Pro Plan?",
      answer: "Our Pro plan includes an advanced Report Builder that lets you combine emissions data with governance frameworks, climate risks, and social metrics. You can select specific frameworks (like AASB S2 or QPP compliance), apply bespoke themes, and let our AI assist in generating professional narratives tailored to your stakeholders."
    },
    {
      question: "Can I track carbon offsets and Net Zero progress?",
      answer: "Yes! You can record purchased carbon offsets, verify their retirement certificates, and apply them directly to your gross emissions. Our dashboard calculates your Net Emissions instantly, helping you clearly demonstrate your trajectory towards Net Zero."
    },
    {
      question: "Do you integrate with Xero or MYOB?",
      answer: "Integrations for Xero and MYOB are coming soon! These connections will automatically bring in your Fuel, Gas, and Electricity bills where possible, further reducing manual data entry and ensuring your carbon footprint is always up to date."
    },
    {
      question: "Is my data secure?",
      answer: "Your data is encrypted and stored right here in Australia. We use a \"Privacy-First\" architecture, meaning even our admin team requires your explicit approval to view your dashboard for support purposes."
    },
    {
      question: "Are you AASB S2 ready?",
      answer: "Lucent Sky ESG is designed to simplify Scope 1 & 2 tracking for SMEs today, while building the foundational data history needed for alignment with AASB S2. As standards evolve, we are enhancing the platform to support Group 3 organisations as they transition toward mandatory climate reporting in FY 2027/28. We already have many AASB S2 style reports available in the Pro plan for testing and feedback."
    },
    {
      question: "Is this tool compliant with the 2026 Queensland Procurement Policy (QPP)?",
      answer: "Yes. While we work nationally, our roots are in the Sunny Coast. Our reports are specifically designed to meet the environmental reporting requirements of the QPP 2026 and the national AASB S2 standards."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="faq-page">
      {/* Hero Section */}
      <section className="pt-20 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-16 bg-[#003B5C]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8 text-[#003B5C]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#003B5C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to know about Lucent Sky ESG, carbon compliance, and the 2026 National Greenhouse Accounts.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                  data-testid={`faq-toggle-${index}`}
                >
                  <span className="font-semibold text-[#003B5C] pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#74A57F] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pb-5"
                  >
                    <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-[#003B5C] to-[#005580]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Start Your Compliance Journey?
          </h2>
          <p className="text-white/80 mb-8">
            Join hundreds of Australian SMEs already using Lucent Sky ESG.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate('/signup')}
              className="bg-[#74A57F] hover:bg-[#5F8575] text-white px-8 py-6 text-lg"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/pricing')}
              variant="outline"
              className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
            >
              View Pricing
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
